import { useEffect, useRef, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const POLL_MS = 8000;

/**
 * Slide-over chat drawer.
 *
 * Props (one of):
 * - threadId: open an existing thread by id (admin clicking a thread row)
 * - bookingId: open or create a booking thread
 * - kind="concierge": open or create the user's concierge channel
 * - counterpartyUserId: when admin opens a specific user's concierge channel
 *
 * onClose(): close the drawer
 */
export default function ChatDrawer({ threadId, bookingId, kind, counterpartyUserId, onClose }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [thread, setThread] = useState(null);
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  // Resolve the thread on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let resolved;
        if (threadId) {
          // Just load messages — thread metadata comes back from /messages endpoint
          const { data } = await api.get(`/messages/threads/${threadId}/messages`);
          if (!alive) return;
          setThread(data.thread);
          setItems(data.items || []);
          return;
        }
        const { data } = await api.post(`/messages/threads/open`, {
          kind: kind || "booking",
          booking_id: bookingId || null,
          counterparty_user_id: counterpartyUserId || null,
        });
        if (!alive) return;
        resolved = data.thread;
        setThread(resolved);
        const msgs = await api.get(`/messages/threads/${resolved.id}/messages`);
        if (!alive) return;
        setItems(msgs.data.items || []);
      } catch (e) {
        if (alive) setError(formatErr(e));
      }
    })();
    return () => { alive = false; };
  }, [threadId, bookingId, kind, counterpartyUserId]);

  // Poll
  useEffect(() => {
    if (!thread) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/messages/threads/${thread.id}/messages`);
        setItems(data.items || []);
      } catch { /* swallow */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [thread?.id]);

  // Auto-scroll on new
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [items.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!thread || !body.trim()) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/messages/threads/${thread.id}/send`, { body: body.trim() });
      setItems((prev) => [...prev, data.message]);
      setBody("");
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-ink-deep/70 backdrop-blur-sm" data-testid="chat-drawer" onClick={onClose}>
      <div className="w-full sm:max-w-md h-full glass-solid flex flex-col border-l hairline" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b hairline">
          <div className="min-w-0">
            <div className="eyebrow text-teal">
              {thread?.kind === "concierge" ? t("chat.concierge_label", "Concierge desk") : t("chat.booking_label", "Booking chat")}
            </div>
            <div className="font-serif-display text-lg truncate" data-testid="chat-title">
              {thread?.title || t("common.loading", "Loading…")}
            </div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-bone" aria-label="Close" data-testid="chat-close">
            <X size={20}/>
          </button>
        </div>

        {error && <div className="text-rose-300 text-xs px-5 py-2">{error}</div>}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
          {items.length === 0 ? (
            <div className="text-center text-muted text-sm py-12">
              {t("chat.empty", "No messages yet — say hello.")}
            </div>
          ) : items.map((m) => {
            const mine = m.author_id === user?.id;
            return (
              <div key={m.id} className={cx("flex flex-col", mine ? "items-end" : "items-start")} data-testid={`msg-${m.id}`}>
                <div className={cx(
                  "rounded-2xl px-3.5 py-2 text-sm max-w-[80%] whitespace-pre-wrap break-words",
                  mine ? "bg-teal text-ink rounded-br-md" : "bg-white/5 border hairline rounded-bl-md"
                )}>
                  {m.body}
                </div>
                <div className="text-[10px] text-muted/60 mt-1 px-1">
                  {!mine && <span className="font-medium">{m.author_name || m.author_role}</span>}{" "}
                  {new Date(m.created_at).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="p-3 border-t hairline flex items-center gap-2" data-testid="chat-form">
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("chat.placeholder", "Write a message…")}
            className="input-bare flex-1"
            data-testid="chat-input"
            maxLength={5000}
          />
          <button type="submit" disabled={busy || !body.trim()}
                  className="h-10 w-10 grid place-items-center rounded-full bg-teal text-ink hover:bg-teal-hover disabled:opacity-50 transition"
                  aria-label="Send"
                  data-testid="chat-send">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
          </button>
        </form>
      </div>
    </div>
  );
}
